// Holds base64-encoded audio chunks streamed via AUDIO_CHUNK_AVAILABLE postMessages
// while a recording is active. Disarmed at all other times — postMessages received
// while disarmed are dropped and counted, so a late chunk from a prior recording's
// tail cannot leak into the next recording's blob.
//
// Sessions are scoped by a non-empty string id (e.g. transcriptSectionId). When a
// chunk arrives with an explicit sessionId tag that doesn't match the currently armed
// session, it's dropped — this protects against a race where the offscreen doc emits
// a final chunk after `arm(nextId)` has fired but before the offscreen has seen the
// session swap. Untagged chunks bypass the session check and are accepted while armed
// (the current Chrome extension doesn't tag chunks; the check is forward-compat).
//
// History: introduced for EN-719 (WebM upload prefix corruption — stale Matroska
// bytes from a prior recording prepended to a new one).

export type PushOutcome = "accepted" | "dropped_disarmed" | "dropped_wrong_session";

export interface ChunkAccumulatorState {
    armed: boolean;
    sessionId: string | null;
    chunkCount: number;
    // Per-session counters; reset on every arm() so breadcrumbs reflect the most
    // recently completed session, not page-load cumulative noise.
    droppedDisarmed: number;
    droppedWrongSession: number;
}

// Param names carry leading underscores because the project's eslint `no-unused-vars`
// rule requires it for unused identifiers, and TypeScript treats type-only function
// parameter positions as unused. The names are documentation only — they're ignored at
// runtime by the implementation in createAudioChunkAccumulator.
export interface AudioChunkAccumulator {
    arm: (_sessionId: string) => void;
    disarm: () => void;
    pushChunk: (_chunk: string, _sessionId?: string) => PushOutcome;
    snapshotAndClear: () => string[];
    state: () => ChunkAccumulatorState;
}

export const createAudioChunkAccumulator = (): AudioChunkAccumulator => {
    let armed = false;
    let sessionId: string | null = null;
    let chunks: string[] = [];
    let droppedDisarmed = 0;
    let droppedWrongSession = 0;

    return {
        arm(nextSessionId: string) {
            if (typeof nextSessionId !== "string" || nextSessionId.length === 0) {
                // An empty sessionId would silently route all subsequent tagged chunks to
                // dropped_wrong_session, dropping the entire session. Treat as a bug.
                throw new Error("audioChunkAccumulator.arm requires a non-empty sessionId");
            }
            armed = true;
            sessionId = nextSessionId;
            chunks = [];
            droppedDisarmed = 0;
            droppedWrongSession = 0;
        },
        disarm() {
            armed = false;
            // Intentionally keep sessionId so the next state() snapshot still shows what we
            // just finished. While disarmed, the armed-guard runs first in pushChunk, so the
            // retained sessionId has no effect on routing (every push goes to dropped_disarmed
            // regardless of chunk tag).
        },
        pushChunk(chunk, chunkSessionId): PushOutcome {
            if (!armed) {
                droppedDisarmed += 1;
                return "dropped_disarmed";
            }
            if (chunkSessionId !== undefined && chunkSessionId !== sessionId) {
                droppedWrongSession += 1;
                return "dropped_wrong_session";
            }
            chunks.push(chunk);
            return "accepted";
        },
        snapshotAndClear() {
            // Returns whatever's currently buffered and resets the internal array. Safe to
            // call in any state (armed or disarmed): a disarmed accumulator simply returns []
            // because pushChunk drops everything while disarmed. The closure rebinds `chunks`
            // to a new array before returning the old one, so caller mutation of the snapshot
            // can't affect future accumulator operations.
            const snapshot = chunks;
            chunks = [];
            return snapshot;
        },
        state() {
            return {
                armed,
                sessionId,
                chunkCount: chunks.length,
                droppedDisarmed,
                droppedWrongSession,
            };
        },
    };
};
